/**
 * Finance module access & screen helpers.
 *
 * UI/RLS access = org modules.finance.enabled + membership finance_access.
 * Producer posting = org modules.finance.enabled + active membership
 * (allowlisted event types; see finance_post_event).
 * Independent of Client Workspace authorization.
 */

import { can, CAPABILITY, resolveOrgPolicies } from "./permissions";

/** Canonical Nexora Systems operations organization id (platform seed). */
export const NEXORA_OPERATIONS_ORGANIZATION_ID =
  "c3a10001-0001-4001-8001-000000000001";

export const FINANCE_SCREENS = Object.freeze([
  "finance_dashboard",
  "finance_accounts",
  "finance_transactions",
  "finance_money_in",
  "finance_money_out",
  "finance_transfer",
  "financial_position",
]);

export const FINANCE_SCREEN_SET = new Set(FINANCE_SCREENS);

/**
 * Read modules.finance.enabled from org settings / policies.
 */
export function isFinanceModuleEnabled(orgSettingsOrPolicies = {}) {
  if (orgSettingsOrPolicies.financeModuleEnabled === true) {
    return true;
  }

  const modules = orgSettingsOrPolicies.modules;
  if (modules?.finance?.enabled === true) {
    return true;
  }

  return false;
}

/**
 * Sync gate for Finance nav / screens via capabilities + module flag.
 * Requires actor.finance_access (module permission).
 */
export function actorCanAccessFinance(actor, orgPolicies) {
  const policies = orgPolicies || resolveOrgPolicies();
  if (!policies.financeModuleEnabled) {
    return false;
  }
  return can(actor, CAPABILITY.FINANCE_MODULE, {}, policies);
}

/**
 * Server-backed gate: actor has finance_access in a Finance-enabled org.
 * Fail-closed. Used for Finance UI/data reads — not producer posting.
 */
export async function actorHasFinanceAccess(client) {
  if (!client?.rpc) {
    return false;
  }

  try {
    const { data, error } = await client.rpc("cp_actor_has_finance_access");
    if (error) {
      console.error("[Finance] access check failed:", error);
      return false;
    }
    return data === true;
  } catch (err) {
    console.error("[Finance] access check failed:", err);
    return false;
  }
}

/**
 * Server-backed gate: actor may post producer events into Finance
 * (module enabled for an active membership). Does not require finance_access.
 * Fail-closed.
 */
export async function actorCanPostFinanceEvents(client) {
  if (!client?.rpc) {
    return false;
  }

  try {
    const { data, error } = await client.rpc("cp_actor_can_post_finance_events");
    if (error) {
      console.error("[Finance] producer post check failed:", error);
      return false;
    }
    return data === true;
  } catch (err) {
    console.error("[Finance] producer post check failed:", err);
    return false;
  }
}

/**
 * Resolve organization id for stamping Finance rows.
 * Prefer the actor's current organization (multi-tenant).
 */
export function resolveFinanceOrganizationIdFromActor(actor) {
  return actor?.organization_id || null;
}

/** @deprecated Use resolveFinanceOrganizationIdFromActor — ops pointer is not the stamp source. */
export async function resolveFinanceOrganizationId(client, actor) {
  if (actor?.organization_id) {
    return actor.organization_id;
  }

  if (!client?.rpc) {
    return NEXORA_OPERATIONS_ORGANIZATION_ID;
  }

  try {
    const { data, error } = await client.rpc(
      "cp_get_nexora_operations_organization_id",
    );
    if (error) {
      console.error("[Finance] ops org lookup failed:", error);
      return NEXORA_OPERATIONS_ORGANIZATION_ID;
    }
    return data || NEXORA_OPERATIONS_ORGANIZATION_ID;
  } catch (err) {
    console.error("[Finance] ops org lookup failed:", err);
    return NEXORA_OPERATIONS_ORGANIZATION_ID;
  }
}

export function isFinanceScreen(screenId) {
  return FINANCE_SCREEN_SET.has(screenId);
}

export function isFinanceMode(mode) {
  return mode === "finance";
}

/** @deprecated Prefer actorCanAccessFinance + capabilities */
export function actorMatchesNexoraOperationsOrg(actor) {
  if (!actor) {
    return false;
  }
  if (actor.organization_id === NEXORA_OPERATIONS_ORGANIZATION_ID) {
    return true;
  }
  const membershipIds = Array.isArray(actor.organization_ids)
    ? actor.organization_ids
    : Array.isArray(actor.active_organization_ids)
      ? actor.active_organization_ids
      : [];
  return membershipIds.some((id) => id === NEXORA_OPERATIONS_ORGANIZATION_ID);
}
